import { Dispatch } from 'react';
import api from '../API/api';
import { AppAction } from '../Components/App/appReducer';
import { ChatMessage } from '../Components/Shared/API/ApiTypes';

// Default bound for the implicit "close-the-widget-right-after-a-nudge" dismiss
// heuristic (ADR #55). Closing/minimizing within this many seconds of a *shown*
// nudge, with no reply in the window, counts as an implicit dismissal. Client
// constant — not tenant-configurable and not published to S3 in v1.
export const DISMISS_CLOSE_WINDOW_SECONDS = 10;

// Distinguishes the two dismissal gestures for analytics (ADR #55): the teaser ✕
// (explicit) vs the close-after-nudge heuristic (implicit).
export type DismissKind = 'explicit' | 'implicit';

// Pure predicate for the implicit close-after-nudge dismiss (ADR #55). A widget
// close counts as an implicit dismissal only when a nudge was *shown*
// (proactiveShownAt set), the close is within DISMISS_CLOSE_WINDOW_SECONDS of it,
// and the user has not replied (no direction-0 message at/after the shown time)
// in that window. Otherwise it is an ordinary close. Extracted so the heuristic is
// unit-testable without mounting the provider.
export const shouldImplicitlyDismiss = (
    args: { proactiveShownAt: number | null; messages: ChatMessage[] },
    now: number
): boolean => {
    const shownAt = args.proactiveShownAt;
    if (!shownAt) return false;
    if (now - shownAt > DISMISS_CLOSE_WINDOW_SECONDS * 1000) return false;
    const repliedSinceShown = args.messages.some(
        (m) => m.direction === 0 && new Date(m.time).getTime() >= shownAt
    );
    return !repliedSinceShown;
};

// Shared proactive-dismiss flow. This responsibility lives in the iframe (the
// chat) because the dismiss endpoint, analytics, and proactive state machine all
// require iframe-only inputs (sessionid, IndexName, the proactive API key, the
// reducer). It is invoked from the launcher teaser ✕ (explicit) and the implicit
// close heuristic, both via postMessage bridges handled in AppContext.
export type DismissProactiveArgs = {
    sessionid: string;
    index: string;
    kind: DismissKind;
    proactiveMessageId?: string;
    pageUrl?: string;
};

export const dismissProactive = async (
    args: DismissProactiveArgs,
    dispatch: Dispatch<AppAction>
): Promise<void> => {
    try {
        await api.proactive.dismiss({
            sessionid: args.sessionid,
            index: args.index,
            proactiveMessageId: args.proactiveMessageId,
        });
    } catch (_) {}
    try {
        // Composite eventvalue "<kind>:<proactiveMessageId>" (ADR #55): the prefix
        // separates active rejection (explicit) from incidental close (implicit);
        // the suffix preserves correlation to the on-screen nudge. Fallback
        // "<kind>:unknown" when the id is absent.
        api.logEvent.logEvent({
            eventtype: 'ProactiveDismissed',
            eventvalue: `${args.kind}:${args.proactiveMessageId ?? 'unknown'}`,
            indexname: args.index,
            sessionid: args.sessionid,
            pageurl: args.pageUrl || '',
        });
    } catch (_) {}
    dispatch({ type: 'dismissProactive' });
};
